import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Block,
  BlockTitle,
  Button,
  Link,
  List,
  ListItem,
  PageContent,
  Preloader,
  Sheet,
  Toolbar,
} from "framework7-react";
import { useBackClose } from "../../hooks/use-back-close";
import { vibrate } from "../../hooks/use-haptics";
import { useToast } from "../../hooks/use-toast";
import { selectMe } from "../../redux/reducer/app-slice";
import {
  loadNotificationSettings,
  saveNotificationSettings,
} from "../../redux/reducer/notifications";
import { isIos } from "../../utils/install-platform";
import { pushSupport } from "../../utils/push-subscription";
import {
  disablePushOnDevice,
  enablePush,
  refreshPushState,
  usePushState,
} from "./push-state";

export const ASSIGNMENT_HINT =
  "Wähle aus, wer du bist, dann wirst du nur benachrichtigt, wenn du beteiligt bist, und siehst deinen Anteil.";

// A plain controlled checkbox: F7's Toggle re-emits changes for prop updates.
const Switch = ({ className, checked, disabled, onChange }) => (
  <label className={`toggle ${className}${disabled ? " disabled" : ""}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="toggle-icon" />
  </label>
);

const Note = ({ className, children }) => (
  <Block className={className}>{children}</Block>
);

const DeniedHelp = () => (
  <div className="notifications-denied">
    <Block>
      Benachrichtigungen sind für Split It blockiert. So erlaubst du sie
      wieder:
    </Block>
    {isIos() ? (
      <Block className="install-note">
        Einstellungen → Mitteilungen → Split It → „Mitteilungen erlauben“
        einschalten.
      </Block>
    ) : (
      <Block className="install-note">
        Installierte App: lange auf das App-Symbol tippen → App-Info →
        Benachrichtigungen. Im Browser: auf das Symbol links neben der Adresse
        tippen → Berechtigungen → Benachrichtigungen zulassen.
      </Block>
    )}
    <Block className="install-note">Öffne danach diese Einstellung erneut.</Block>
  </div>
);

const Unavailable = ({ push }) => {
  if (push.support === pushSupport.needsInstall) {
    return (
      <Note className="notifications-install">
        Auf dem iPhone gibt es Benachrichtigungen nur in der installierten App.
        Tippe in Safari auf „Teilen“ → „Zum Home-Bildschirm“ und öffne Split It
        dann vom Home-Bildschirm.
      </Note>
    );
  }
  if (push.support === pushSupport.unsupported) {
    return (
      <Note className="notifications-unsupported">
        Dieser Browser kann keine Benachrichtigungen anzeigen.
      </Note>
    );
  }
  return (
    <Note className="notifications-unsupported">
      Benachrichtigungen sind auf dem Server noch nicht eingerichtet.
    </Note>
  );
};

const isAvailable = (push) =>
  push.support === pushSupport.supported && push.key?.enabled !== false;

const NotificationsSheet = ({ opened, onClose, onChooseMe, group }) => {
  const releaseBack = useBackClose(opened, onClose);
  const dispatch = useDispatch();
  const { showError } = useToast();
  const push = usePushState();
  const me = useSelector(selectMe);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const groupId = group?.id;

  useEffect(() => {
    if (!opened) return undefined;
    let active = true;
    setSettings(null);
    refreshPushState();
    if (groupId) {
      dispatch(
        loadNotificationSettings(
          groupId,
          (loaded) => active && setSettings(loaded),
          (message) => active && showError(message),
        ),
      );
    }
    return () => {
      active = false;
    };
  }, [opened, groupId, dispatch, showError]);

  const save = (next) => {
    const previous = settings;
    setSettings(next);
    return new Promise((resolve) =>
      dispatch(
        saveNotificationSettings(
          groupId,
          next,
          (saved) => {
            setSettings(saved);
            vibrate();
            resolve(true);
          },
          (message) => {
            setSettings(previous);
            showError(message);
            resolve(false);
          },
        ),
      ),
    );
  };

  const switchGroup = async (on) => {
    if (!settings) return;
    if (!on) {
      await save({ ...settings, enabled: false });
      return;
    }
    const pending = { ...settings, enabled: true };
    setSettings(pending);
    setBusy(true);
    const result = push.endpoint ? { subscribed: true } : await enablePush();
    setBusy(false);
    if (result.error) showError(result.error);
    if (!result.subscribed) {
      setSettings(settings);
      return;
    }
    await save(pending);
  };

  const switchInvolved = (onlyInvolved) => {
    if (!settings) return;
    save({ ...settings, only_involved: onlyInvolved });
  };

  const switchDeviceOff = async () => {
    setBusy(true);
    const { error } = await disablePushOnDevice();
    setBusy(false);
    if (error) showError(error);
    else vibrate();
  };

  const subscribed = Boolean(push.endpoint);
  const groupOn = subscribed && Boolean(settings?.enabled);
  const denied = push.permission === "denied";
  const loading = !push.checked || !settings;

  const body = () => {
    if (push.checked && !isAvailable(push)) return <Unavailable push={push} />;
    if (denied) return <DeniedHelp />;
    if (loading) {
      return (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      );
    }
    return (
      <>
        <Block>
          Du erfährst, wenn andere in „{group?.name}“ eine Ausgabe oder Zahlung
          eintragen oder neue Leute dazukommen.
        </Block>
        <List strong inset dividers>
          <ListItem title="Für diese Gruppe">
            <Switch
              slot="after"
              className="notifications-group-toggle"
              checked={groupOn}
              disabled={busy}
              onChange={switchGroup}
            />
          </ListItem>
          <ListItem title="Nur wenn ich beteiligt bin">
            <Switch
              slot="after"
              className="notifications-involved-toggle"
              checked={Boolean(settings.only_involved)}
              disabled={busy || !groupOn || me == null}
              onChange={switchInvolved}
            />
          </ListItem>
        </List>
        {me == null && (
          <Block className="install-note notifications-hint">
            {ASSIGNMENT_HINT}
            <Button
              small
              className="notifications-choose-me"
              onClick={onChooseMe}
            >
              Auswählen, wer ich bin
            </Button>
          </Block>
        )}
        {subscribed && (
          <Block>
            <Button
              small
              className="notifications-device-off"
              disabled={busy}
              onClick={switchDeviceOff}
            >
              Auf diesem Gerät für alle Gruppen ausschalten
            </Button>
          </Block>
        )}
      </>
    );
  };

  return (
    <Sheet
      className="notifications-sheet"
      style={{ height: "auto" }}
      opened={opened}
      onSheetClose={releaseBack}
      onSheetClosed={onClose}
      swipeToClose
      backdrop
    >
      <Toolbar>
        <div className="left"></div>
        <div className="right">
          <Link sheetClose iconOnly aria-label="Schließen">
            <i className="icon icon-close" />
          </Link>
        </div>
      </Toolbar>
      <PageContent>
        <BlockTitle large>Benachrichtigungen</BlockTitle>
        {body()}
      </PageContent>
    </Sheet>
  );
};

export default NotificationsSheet;
